import SideCell from './SideCell.svelte';
import type { CreateLegsQueryOptions, LegsQueryOptions } from './types';
import type { AgPromise, ICellRendererComp, ICellRendererParams } from '@ag-grid-community/core';
import { signal } from '@preact/signals-react';
import { container } from 'src/StaticContainer';
import { mount, unmount } from 'svelte';

const log = container.get('Logger').getSubLogger({ name: 'SideCellRenderer' });

export interface SideCellRendererOptions<TData> {
    /**
     * The field name in the row data that contains the side value (e.g., "BUY", "SELL", "MULTI").
     */
    sideField: string;

    /**
     * Factory function to create query options for fetching legs data.
     * This allows different widgets to provide their own data source:
     * - BlotterWidget: Returns query options with legs from row data (synchronous lookup)
     * - OrdersWidget: Returns query options that fetch from API (async)
     *
     * The returned query options are stored in a Signal, which updates when row data changes.
     * React-query will determine when to actually re-fetch based on the queryKey.
     *
     * @param rowData The row data for this cell
     * @returns Query options or null if legs are not available for this row
     */
    createQueryOptions: CreateLegsQueryOptions<TData>;

    /**
     * Function to determine if this row represents a multi-leg order.
     * If not provided, defaults to checking if side === 'MULTI' (case-insensitive).
     *
     * @param rowData The row data for this cell
     * @returns true if this is a multi-leg order
     */
    isMultiLeg?: (rowData: TData) => boolean;
}

/**
 * Creates an AG Grid cell renderer for the Side column that shows a legs info icon
 * for multi-leg orders.
 *
 * @example
 * // For BlotterWidget (synchronous data from row)
 * const SideCellRenderer = makeSideCellRenderer<BlotterProcessedRow>({
 *     sideField: 'side',
 *     createQueryOptions: (rowData) => {
 *         if (!rowData.legs) return null;
 *         return {
 *             queryKey: ['blotter-order-legs', rowData.id],
 *             queryFn: () => Promise.resolve(rowData.legs),
 *             staleTime: Infinity,
 *         };
 *     },
 * });
 *
 * @example
 * // For OrdersWidget (async data from API)
 * const SideCellRenderer = makeSideCellRenderer<OrderRowData>({
 *     sideField: 'Side',
 *     createQueryOptions: (rowData) => ({
 *         queryKey: ['order-legs', rowData.orderId],
 *         queryFn: () => fetchLegsFromAPI(rowData.orderId),
 *     }),
 * });
 */
export const makeSideCellRenderer = <TData>(options: SideCellRendererOptions<TData>): { new (): ICellRendererComp } => {
    const { sideField, createQueryOptions, isMultiLeg: customIsMultiLeg } = options;

    const defaultIsMultiLeg = (rowData: TData): boolean => {
        const sideValue = (rowData as Record<string, unknown>)[sideField];
        if (typeof sideValue === 'string') {
            return sideValue.toUpperCase() === 'MULTI';
        }
        return false;
    };

    const isMultiLegFn = customIsMultiLeg ?? defaultIsMultiLeg;

    return class CellRenderer implements ICellRendererComp {
        #div: HTMLSpanElement = document.createElement('span');
        #root: ReturnType<typeof mount> | undefined;

        #sideValue = signal<string>('');
        #isMultiLeg = signal<boolean>(false);
        #queryOptions = signal<LegsQueryOptions>(null);

        getGui(): HTMLElement {
            this.#div.className = 'flex items-center';
            return this.#div;
        }

        init(params: ICellRendererParams<TData>): void | AgPromise<void> {
            this.#updateFromParams(params);

            this.#root = mount(SideCell, {
                target: this.#div,
                props: {
                    sideValue: this.#sideValue,
                    isMultiLeg: this.#isMultiLeg,
                    queryOptions: this.#queryOptions,
                },
            });
        }

        refresh(params: ICellRendererParams<TData>): boolean {
            this.#updateFromParams(params);
            return true;
        }

        destroy(): void {
            if (this.#root) {
                unmount(this.#root);
            }
        }

        #updateFromParams(params: ICellRendererParams<TData>): void {
            const rowData = params.data;
            const rowId = params.node?.id;

            if (TRACE) {
                log.trace('updateFromParams called', { rowId });
            }

            if (!rowData) {
                this.#sideValue.value = '';
                this.#isMultiLeg.value = false;
                this.#queryOptions.value = null;
                return;
            }

            // Update side value
            const sideValue = (rowData as Record<string, unknown>)[sideField];
            const resolvedSideValue = typeof sideValue === 'string' ? sideValue : String(sideValue ?? '');
            this.#sideValue.value = resolvedSideValue;

            if (TRACE) {
                log.trace('Resolved side value', { rowId, sideValue: resolvedSideValue });
            }

            // Check if multi-leg
            const isMulti = isMultiLegFn(rowData);
            this.#isMultiLeg.value = isMulti;

            if (TRACE) {
                log.trace('Checked isMultiLeg', { rowId, isMulti });
            }

            // Update query options signal - this allows the hover card to re-fetch if needed
            if (isMulti) {
                const queryOpts = createQueryOptions(rowData);
                this.#queryOptions.value = queryOpts;

                if (TRACE) {
                    log.trace('Updated query options', {
                        rowId,
                        hasQueryOptions: queryOpts !== null,
                    });
                }
            } else {
                this.#queryOptions.value = null;
            }
        }
    };
};
